import { useState, useEffect, useRef } from 'react';
import { Button, Badge, Spinner, Alert, Card, Form, Collapse } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { FaWhatsapp, FaQrcode, FaCheckCircle, FaTimesCircle, FaSync, FaCog, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../services/api';

/**
 * WhatsApp Web connection component
 * Handles QR code display and connection status for WhatsApp Web channels
 */
function WhatsAppWebConnect({ channelId, onConnected, onDisconnected }) {
  const [status, setStatus] = useState('checking'); // checking, disconnected, awaiting_scan, connected
  const [qrCode, setQrCode] = useState(null);
  const [qrImage, setQrImage] = useState(null); // Base64 image for headless mode
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const pollInterval = useRef(null);

  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    replyPolling: { enabled: true, intervalMinutes: 5 },
    autoConvert: { enabled: false },
    dailyLimit: 0,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [channelPhone, setChannelPhone] = useState(null);

  // Check connection status on mount and load settings
  useEffect(() => {
    checkStatus();
    loadSettings();
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [channelId]);

  const loadSettings = async () => {
    try {
      const response = await api.get(`/channels/${channelId}`);
      const channelSettings = response.data.data.settings || {};
      const maskedCreds = response.data.data.maskedCredentials || {};
      if (maskedCreds.phoneNumber) {
        setChannelPhone(maskedCreds.phoneNumber);
      }
      setSettings({
        replyPolling: {
          enabled: channelSettings.replyPolling?.enabled !== false,
          intervalMinutes: channelSettings.replyPolling?.intervalMinutes || 5,
        },
        autoConvert: {
          enabled: channelSettings.autoConvert?.enabled || false,
        },
        dailyLimit: channelSettings.dailyLimit || 0,
      });
    } catch (err) {
      console.error('Failed to load channel settings:', err);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch(`/channels/${channelId}`, { settings });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  const checkStatus = async () => {
    try {
      const response = await api.get(`/channels/${channelId}/whatsapp-web/status`);
      const { status: connStatus, profile: connProfile } = response.data.data;

      if (connStatus === 'CONNECTED') {
        setStatus('connected');
        setProfile(connProfile);
        setQrCode(null);
        setQrImage(null);
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
          pollInterval.current = null;
        }
        onConnected?.();
      } else {
        setStatus('disconnected');
        setProfile(null);
      }
    } catch (err) {
      // Silently set to disconnected - don't show error as this is normal
      setStatus('disconnected');
      setProfile(null);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    setQrCode(null);
    setQrImage(null);

    try {
      const response = await api.post(`/channels/${channelId}/whatsapp-web/connect`);
      const { status: connStatus, qr, qrImage: capturedQrImage, message, profile: connProfile } = response.data.data;

      if (connStatus === 'connected') {
        setStatus('connected');
        setProfile(connProfile);
        toast.success('WhatsApp Web connected successfully');
        onConnected?.();
      } else if (connStatus === 'awaiting_scan') {
        setStatus('awaiting_scan');
        setQrCode(qr);
        setQrImage(capturedQrImage); // Captured QR image for headless mode
        toast.success('QR code ready - scan with your phone');
        // Start polling for status
        startPolling();
      } else {
        setError(message || 'Failed to initialize connection');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to connect WhatsApp Web');
      toast.error('Failed to connect WhatsApp Web');
    } finally {
      setConnecting(false);
    }
  };

  const startPolling = () => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }

    pollInterval.current = setInterval(async () => {
      try {
        const response = await api.get(`/channels/${channelId}/whatsapp-web/status`);
        const { status: connStatus, profile: connProfile } = response.data.data;

        if (connStatus === 'CONNECTED') {
          setStatus('connected');
          setProfile(connProfile);
          setQrCode(null);
          setQrImage(null);
          clearInterval(pollInterval.current);
          pollInterval.current = null;
          toast.success('WhatsApp Web connected!');
          onConnected?.();
        }
      } catch (err) {
        // Keep polling on error
      }
    }, 3000);

    // Stop polling after 2 minutes (QR codes expire)
    setTimeout(() => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
        if (status === 'awaiting_scan') {
          setError('QR code expired. Please try again.');
          setStatus('disconnected');
          setQrCode(null);
          setQrImage(null);
        }
      }
    }, 120000);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp? This will remove the session and you will need to scan the QR code again.')) {
      return;
    }

    setDisconnecting(true);
    try {
      // Delete session completely (disconnects + removes saved session)
      await api.delete(`/channels/${channelId}/whatsapp-web/session`);
      setStatus('disconnected');
      setProfile(null);
      setQrCode(null);
      setQrImage(null);
      toast.success('WhatsApp disconnected successfully');
      onDisconnected?.();
    } catch (err) {
      console.error('WhatsApp disconnect error:', err);
      toast.error(err.response?.data?.error?.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefresh = () => {
    setStatus('checking');
    checkStatus();
  };

  return (
    <Card className="mt-3">
      <Card.Header className="d-flex align-items-center">
        <FaWhatsapp className="text-success me-2" size={20} />
        <span>WhatsApp Web Connection</span>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {status === 'checking' && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Checking connection status...
          </div>
        )}

        {status === 'disconnected' && (
          <div className="text-center py-3">
            <FaTimesCircle className="text-muted mb-3" size={48} />
            <p className="text-muted mb-3">WhatsApp Web is not connected</p>
            <Button
              variant="success"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Opening Browser...
                </>
              ) : (
                <>
                  <FaQrcode className="me-2" />
                  Connect WhatsApp
                </>
              )}
            </Button>
            <p className="text-muted small mt-3">
              This will open a browser window for QR code scanning
            </p>
          </div>
        )}

        {status === 'awaiting_scan' && (qrCode || qrImage) && (
          <div className="text-center py-3">
            <h6 className="mb-3">Scan this QR code with WhatsApp</h6>
            <div className="d-inline-block p-3 bg-white rounded shadow-sm mb-3">
              {qrImage ? (
                // Display captured QR image (for headless mode / production)
                <img
                  src={qrImage}
                  alt="WhatsApp QR Code"
                  style={{ width: 256, height: 256 }}
                />
              ) : qrCode ? (
                // Fall back to QRCodeSVG (for non-headless mode)
                <QRCodeSVG value={qrCode} size={256} level="M" />
              ) : null}
            </div>
            <p className="text-muted small">
              Open WhatsApp on your phone &rarr; Menu &rarr; Linked Devices &rarr; Link a Device
            </p>
            <div className="mt-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Waiting for scan...
            </div>
          </div>
        )}

        {status === 'connected' && (
          <div className="text-center py-3">
            <FaCheckCircle className="text-success mb-3" size={48} />
            <p className="mb-2">
              <Badge bg="success" className="px-3 py-2">
                <FaWhatsapp className="me-1" /> Connected
              </Badge>
            </p>
            {(channelPhone || profile) && (
              <div className="text-muted small mb-3">
                {(channelPhone || profile?.phoneNumber) && (
                  <p className="mb-1">
                    <strong>Phone:</strong> +{channelPhone || profile.phoneNumber}
                  </p>
                )}
                {profile?.name && (
                  <p className="mb-1">
                    <strong>Name:</strong> {profile.name}
                  </p>
                )}
              </div>
            )}
            <div className="d-flex justify-content-center gap-2">
              <Button variant="outline-secondary" size="sm" onClick={handleRefresh} disabled={disconnecting}>
                <FaSync className="me-1" /> Refresh
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleDisconnect} disabled={disconnecting}>
                {disconnecting ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <FaTimesCircle className="me-1" /> Disconnect
                  </>
                )}
              </Button>
            </div>

            {/* Settings Section */}
            <div className="mt-4 pt-3 border-top">
              <Button
                variant="link"
                className="p-0 text-decoration-none d-flex align-items-center"
                onClick={() => setShowSettings(!showSettings)}
              >
                <FaCog className="me-2" />
                <span>Prospect Settings</span>
                {showSettings ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
              </Button>

              <Collapse in={showSettings}>
                <div className="mt-3">
                  <Form>
                    {/* Reply Polling Settings */}
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="replyPolling"
                        label="Enable Reply Polling"
                        checked={settings.replyPolling.enabled}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            replyPolling: { ...settings.replyPolling, enabled: e.target.checked },
                          })
                        }
                      />
                      <Form.Text className="text-muted">
                        Automatically check for replies from messaged prospects
                      </Form.Text>
                    </Form.Group>

                    {settings.replyPolling.enabled && (
                      <Form.Group className="mb-3 ms-4">
                        <Form.Label className="small">Polling Interval (minutes)</Form.Label>
                        <Form.Select
                          size="sm"
                          value={settings.replyPolling.intervalMinutes}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              replyPolling: { ...settings.replyPolling, intervalMinutes: parseInt(e.target.value) },
                            })
                          }
                          style={{ width: '120px' }}
                        >
                          <option value={5}>5 min</option>
                          <option value={10}>10 min</option>
                          <option value={15}>15 min</option>
                          <option value={30}>30 min</option>
                          <option value={60}>60 min</option>
                        </Form.Select>
                      </Form.Group>
                    )}

                    {/* Auto-Convert Settings */}
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="autoConvert"
                        label="Auto-Convert on Reply"
                        checked={settings.autoConvert.enabled}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            autoConvert: { ...settings.autoConvert, enabled: e.target.checked },
                          })
                        }
                      />
                      <Form.Text className="text-muted">
                        Automatically convert prospects to leads when they reply
                      </Form.Text>
                    </Form.Group>

                    {/* Daily Message Limit */}
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">Daily Message Limit</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        min={0}
                        step={10}
                        value={settings.dailyLimit}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            dailyLimit: parseInt(e.target.value) || 0,
                          })
                        }
                        style={{ width: '120px' }}
                        placeholder="0"
                      />
                      <Form.Text className="text-muted">
                        Max messages per day for campaigns. 0 = unlimited. Remaining recipients are automatically sent the next day.
                      </Form.Text>
                    </Form.Group>

                    <div className="d-flex justify-content-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={saveSettings}
                        disabled={savingSettings}
                      >
                        {savingSettings ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Saving...
                          </>
                        ) : (
                          'Save Settings'
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              </Collapse>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default WhatsAppWebConnect;
